import { CSSProperties, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

type Theme = 'day' | 'night';
type Placement = 'hero' | 'run' | 'final';
type SignupReceipt = {
  requestId: string;
  receivedAt: string;
  status: 'registered' | 'confirmation_required' | 'verification_pending' | 'preview';
};

const DASHBOARD_URL = 'https://dashboard.getautomata.ai';
const RECEIPT_KEY = 'getautomata:early-access-receipt';
const ANALYTICS_SESSION_KEY = 'getautomata:acquisition-session';
const PAGE_LOADED_AT = new Date().toISOString();

const PIPELINE = [
  {
    code: 'W01',
    title: 'Find the demand',
    short: 'Signals and source evidence',
    detail: 'GetAutomata researches market signals, audience questions and competitor movement.',
  },
  {
    code: 'W02',
    title: 'Build the campaign',
    short: 'A production-ready plan',
    detail: 'The evidence becomes a 7-30 day plan with topics, angles and daily output targets.',
  },
  {
    code: 'W03',
    title: 'Make the videos',
    short: 'Scripts, scenes and production',
    detail: 'The campaign moves through scripting, creative direction and daily video production.',
  },
  {
    code: 'W04',
    title: 'Review and release',
    short: 'You approve every asset',
    detail: 'Every video waits for your approval before it can move to distribution.',
  },
  {
    code: 'W05',
    title: 'Learn what worked',
    short: 'Verified results return',
    detail: 'Performance evidence returns to the system and informs the next campaign.',
  },
] as const;

const FAQS = [
  {
    question: 'What happens after I join?',
    answer: 'Confirm your email, then watch for product updates and your Mission Control invitation when your access wave opens.',
  },
  {
    question: 'Will I be charged?',
    answer: 'No. This is the pre-launch access list. There is no checkout and no payment today.',
  },
  {
    question: 'How do Pipelines work?',
    answer: 'You buy a Pipeline once and keep it in your library. That Pipeline can then run campaigns in Mission Control.',
  },
  {
    question: 'Why do campaign runs use credits?',
    answer: 'Pipeline ownership and campaign operation are separate. Credits cover each production run and come from a subscription or top-up.',
  },
] as const;

function analyticsSessionId() {
  try {
    const existing = sessionStorage.getItem(ANALYTICS_SESSION_KEY);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(ANALYTICS_SESSION_KEY, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
}

function track(event: string, placement: string, metadata: Record<string, unknown> = {}) {
  const query = new URLSearchParams(window.location.search);
  let referrerHost = '';
  try { referrerHost = document.referrer ? new URL(document.referrer).hostname : ''; } catch { referrerHost = ''; }
  const payload = JSON.stringify({
    event,
    placement,
    metadata,
    sessionId: analyticsSessionId(),
    eventKey: crypto.randomUUID(),
    referrerHost,
    utm: {
      source: query.get('utm_source') || '',
      medium: query.get('utm_medium') || '',
      campaign: query.get('utm_campaign') || '',
    },
  });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/acquisition-event', new Blob([payload], { type: 'application/json' }));
      return;
    }
    void fetch('/api/acquisition-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    });
  } catch {
    // Analytics must never block the acquisition path.
  }
}

function persistReceipt(receipt: SignupReceipt | null) {
  try {
    if (receipt) sessionStorage.setItem(RECEIPT_KEY, JSON.stringify(receipt));
    else sessionStorage.removeItem(RECEIPT_KEY);
  } catch {
    // The server-side registration remains authoritative.
  }
}

function readReceipt(): SignupReceipt | null {
  try {
    const raw = sessionStorage.getItem(RECEIPT_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as SignupReceipt;
    if (!value.requestId || !value.receivedAt || !['registered', 'confirmation_required', 'verification_pending', 'preview'].includes(value.status)) return null;
    return value;
  } catch {
    return null;
  }
}

function readInitialReceipt(): SignupReceipt | null {
  const stored = readReceipt();
  if (stored) return stored;
  const params = new URLSearchParams(window.location.search);
  const requestId = params.get('receipt') || '';
  if (params.get('confirmed') !== '1' || !requestId) return null;
  return { requestId, receivedAt: PAGE_LOADED_AT, status: 'verification_pending' };
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <a className={`brand${compact ? ' brand--compact' : ''}`} href="#top" aria-label="GetAutomata home">
      <svg className="brand-mark" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
        <path className="brand-mark__frame" d="M8 1.5h22.5L38.5 9v22.5L32 38.5H8L1.5 32V8z" />
        <path className="brand-mark__signal" d="M4.5 28h4.4M31.2 12H35.5M31.2 20H35.5M31.2 28H35.5" />
        <path className="brand-mark__glyph" d="M19.9 7.2 30 31h-5.2l-1.7-4.4h-6.7L14.7 31H9.8L19.9 7.2Zm0 10.3-2 5.1h4Z" />
        <circle className="brand-mark__node" cx="35.5" cy="12" r="1.5" />
        <circle className="brand-mark__node" cx="35.5" cy="20" r="1.5" />
        <circle className="brand-mark__node" cx="35.5" cy="28" r="1.5" />
      </svg>
      <span className="brand-wordmark">
        <span className="brand-wordmark__get">GET</span>
        <span className="brand-wordmark__name">AUTOMATA</span>
      </span>
    </a>
  );
}

function ThemeToggle({ theme, onChange }: { theme: Theme; onChange: (theme: Theme) => void }) {
  const next = theme === 'night' ? 'day' : 'night';
  return (
    <button className="theme-toggle" type="button" onClick={() => onChange(next)} aria-label={`Switch to ${next} mode`}>
      <span aria-hidden="true">{theme === 'night' ? 'NIGHT' : 'DAY'}</span>
      <i aria-hidden="true" />
    </button>
  );
}

function Header({ theme, onTheme, onAccess }: { theme: Theme; onTheme: (theme: Theme) => void; onAccess: () => void }) {
  return (
    <header className="site-header">
      <Brand compact />
      <div className="header-actions">
        <ThemeToggle theme={theme} onChange={onTheme} />
        <button className="header-access" type="button" onClick={onAccess}>GET EARLY ACCESS</button>
      </div>
    </header>
  );
}

function AccessForm({ placement, onSuccess }: { placement: Placement; onSuccess: (receipt: SignupReceipt) => void }) {
  const startedAt = useRef(0);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setState('error');
      setMessage('Enter a valid email address.');
      return;
    }
    if (!consent) {
      setState('error');
      setMessage('Confirm that we may send pre-launch product emails.');
      return;
    }

    setState('submitting');
    setMessage('Registering your access request.');
    const query = new URLSearchParams(window.location.search);
    const form = event.currentTarget;
    const data = new FormData(form);
    track('signup_submit', placement);

    try {
      const response = await fetch('/api/early-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email: normalizedEmail,
          consent: true,
          companyWebsite: data.get('companyWebsite') || '',
          formStartedAt: startedAt.current || Date.now(),
          placement,
          utm: {
            source: query.get('utm_source') || '',
            medium: query.get('utm_medium') || '',
            campaign: query.get('utm_campaign') || '',
            content: query.get('utm_content') || '',
          },
        }),
      });
      const payload = await response.json().catch(() => null) as { ok?: boolean; message?: string; receipt?: SignupReceipt } | null;
      if (!response.ok || !payload?.ok || !payload.receipt) throw new Error(payload?.message || 'Registration is temporarily unavailable.');
      persistReceipt(payload.receipt);
      track('signup_success', placement, { status: payload.receipt.status });
      onSuccess(payload.receipt);
    } catch (error) {
      setState('error');
      setMessage(error instanceof Error ? error.message : 'Registration is temporarily unavailable.');
      track('signup_error', placement, { reason: 'provider_or_network' });
    }
  }

  return (
    <form className="access-form" onSubmit={submit} onFocusCapture={() => { if (!startedAt.current) startedAt.current = Date.now(); }} noValidate>
      <label htmlFor={`access-email-${placement}`}>Work email</label>
      <input
        id={`access-email-${placement}`}
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder="you@company.com"
        value={email}
        onChange={(event) => { setEmail(event.target.value); if (state === 'error') { setState('idle'); setMessage(''); } }}
        disabled={state === 'submitting'}
        aria-invalid={state === 'error' && message.toLowerCase().includes('email') ? true : undefined}
        aria-describedby={`access-status-${placement}`}
        required
      />
      <div className="form-guard" aria-hidden="true">
        <label htmlFor={`website-${placement}`}>Company website</label>
        <input id={`website-${placement}`} name="companyWebsite" type="text" tabIndex={-1} autoComplete="off" />
      </div>
      <label className="consent-row">
        <input
          type="checkbox"
          checked={consent}
          onChange={(event) => { setConsent(event.target.checked); if (state === 'error') { setState('idle'); setMessage(''); } }}
          disabled={state === 'submitting'}
          required
        />
        <span aria-hidden="true" />
        <small>Send me pre-launch product emails. Unsubscribe anytime. <a href="https://getautomata.ai/privacy" target="_blank" rel="noreferrer">Privacy</a></small>
      </label>
      <button className="primary-action primary-action--form" type="submit" disabled={state === 'submitting'}>
        {state === 'submitting' ? 'REGISTERING' : 'GET EARLY ACCESS'} <span aria-hidden="true">↗</span>
      </button>
      <p className={state === 'error' ? 'form-status form-status--error' : 'form-status'} id={`access-status-${placement}`} aria-live="polite">
        {message || 'No card. No checkout. Unsubscribe anytime.'}
      </p>
    </form>
  );
}

function AccessSheet({ open, placement, onClose, onSuccess }: { open: boolean; placement: Placement; onClose: () => void; onSuccess: (receipt: SignupReceipt) => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.setTimeout(() => closeRef.current?.focus(), 20);
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="sheet-shell" role="dialog" aria-modal="true" aria-labelledby="access-title">
      <button className="sheet-backdrop" type="button" onClick={onClose} aria-label="Close early access form" />
      <section className="access-sheet">
        <header>
          <span>PRE-LAUNCH ACCESS</span>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close">CLOSE</button>
        </header>
        <div className="sheet-copy">
          <h2 id="access-title">Get early access to Mission Control.</h2>
          <p>Confirm your email. We will show you how GetAutomata works and send your invitation when access opens.</p>
        </div>
        <AccessForm placement={placement} onSuccess={onSuccess} />
      </section>
    </div>
  );
}

function Hero({ reducedMotion, onAccess }: { reducedMotion: boolean; onAccess: () => void }) {
  return (
    <section className="hero" id="top">
      <div className="hero-media" aria-hidden="true">
        <video
          autoPlay={!reducedMotion}
          muted
          loop
          playsInline
          preload="metadata"
          poster="/media/commissioning-poster-v3.webp"
        >
          <source src="/media/commissioning-field-v3.mp4" type="video/mp4" />
        </video>
      </div>
      <div className="hero-shield" aria-hidden="true" />
      <div className="hero-content">
        <p className="hero-label">START WITH ONE CAMPAIGN BRIEF</p>
        <h1>30 DAYS OF<br /><span>VIDEO.</span></h1>
        <p className="hero-copy">GetAutomata researches, plans, scripts and produces 3-5 videos a day. You approve every release.</p>
        <div className="hero-actions">
          <button className="primary-action" type="button" onClick={onAccess}>GET EARLY ACCESS <span aria-hidden="true">↗</span></button>
          <a className="text-action" href="#pipeline" onClick={() => track('demo_activation', 'hero')}>WATCH THE PIPELINE <span aria-hidden="true">↓</span></a>
        </div>
      </div>
      <div className="hero-outcome" aria-label="Campaign production target">
        <strong>7-30</strong><span>campaign days</span>
        <strong>3-5</strong><span>videos each day</span>
        <strong>YOU</strong><span>approve publishing</span>
      </div>
    </section>
  );
}

function PipelineConsole() {
  const [active, setActive] = useState(0);
  const [running, setRunning] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => () => { if (timer.current) window.clearInterval(timer.current); }, []);

  function run() {
    if (timer.current) window.clearInterval(timer.current);
    setActive(0);
    setRunning(true);
    track('pipeline_interaction', 'machine', { action: 'run' });
    timer.current = window.setInterval(() => {
      setActive((current) => {
        if (current >= PIPELINE.length - 1) {
          if (timer.current) window.clearInterval(timer.current);
          timer.current = null;
          setRunning(false);
          return current;
        }
        return current + 1;
      });
    }, 1300);
  }

  return (
    <section className="pipeline-section" id="pipeline">
      <div className="section-copy">
        <h2>Give it the brief. Get back a campaign.</h2>
        <p>The Pipeline handles research, planning, production and performance tracking in one run.</p>
      </div>
      <div className={running ? 'pipeline-console is-running' : 'pipeline-console'}>
        <div className="console-head">
          <span>CAMPAIGN RUN</span>
          <strong>{running ? 'IN PRODUCTION' : active === PIPELINE.length - 1 ? 'READY TO REVIEW' : 'STANDBY'}</strong>
        </div>
        <div className="pipeline-rail" style={{ '--active-stage': active } as CSSProperties}>
          <div className="pipeline-track" aria-hidden="true"><i /></div>
          {PIPELINE.map((stage, index) => (
            <button
              key={stage.code}
              className={index <= active ? `pipeline-node${index === active ? ' is-active' : ' is-complete'}` : 'pipeline-node'}
              type="button"
              onClick={() => { setActive(index); setRunning(false); if (timer.current) window.clearInterval(timer.current); track('pipeline_interaction', 'stage', { stage: stage.code }); }}
              aria-pressed={index === active}
            >
              <span>{stage.code}</span>
              <i aria-hidden="true" />
            </button>
          ))}
        </div>
        <article className="stage-readout" aria-live="polite">
          <span>{PIPELINE[active].code} / {PIPELINE[active].short}</span>
          <h3>{PIPELINE[active].title}</h3>
          <p>{PIPELINE[active].detail}</p>
        </article>
        <button className="run-button" type="button" onClick={run} disabled={running}>
          {running ? 'CAMPAIGN RUNNING' : 'RUN THE CAMPAIGN'} <span aria-hidden="true">{running ? '•••' : '▶'}</span>
        </button>
      </div>
    </section>
  );
}

function ProductionFilm({ reducedMotion }: { reducedMotion: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  async function toggle() {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      await video.play();
      setPlaying(true);
      track('film_play', 'presentation');
    } else {
      video.pause();
      setPlaying(false);
    }
  }

  return (
    <section className="film-section">
      <div className="film-frame">
        <video
          ref={videoRef}
          muted
          playsInline
          loop
          preload="none"
          poster="/media/commissioning-poster-v3.webp"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        >
          <source src="/media/commissioning-field-v3.mp4" type="video/mp4" />
        </video>
        {!reducedMotion && (
          <button type="button" className="film-control" onClick={toggle} aria-label={playing ? 'Pause production film' : 'Play production film'}>
            <span aria-hidden="true">{playing ? 'Ⅱ' : '▶'}</span>
            <b>{playing ? 'PAUSE FILM' : 'PLAY FILM'}</b>
          </button>
        )}
        <div className="film-caption"><span>ONE FINISHED VIDEO</span><strong>00:15</strong></div>
      </div>
      <div className="section-copy film-copy">
        <h2>This is one finished video.</h2>
        <p>A full campaign produces 3-5 directed videos like this every day.</p>
      </div>
    </section>
  );
}

function OutputCalculator({ onAccess }: { onAccess: () => void }) {
  const [days, setDays] = useState<7 | 14 | 30>(7);
  const [daily, setDaily] = useState<3 | 4 | 5>(3);
  const total = days * daily;
  const slots = useMemo(() => Array.from({ length: Math.min(5, daily) }, (_, index) => index + 1), [daily]);

  return (
    <section className="output-section" id="output">
      <div className="section-copy">
        <h2>Pick the pace. See the output.</h2>
        <p>Set a campaign target. GetAutomata builds the production schedule around it.</p>
      </div>
      <div className="output-control">
        <fieldset>
          <legend>Campaign length</legend>
          <div className="segmented-control">
            {([7, 14, 30] as const).map((value) => (
              <button key={value} type="button" aria-pressed={days === value} onClick={() => { setDays(value); track('pipeline_interaction', 'campaign_length', { days: value }); }}>{value} DAYS</button>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend>Videos each day</legend>
          <div className="segmented-control">
            {([3, 4, 5] as const).map((value) => (
              <button key={value} type="button" aria-pressed={daily === value} onClick={() => { setDaily(value); track('pipeline_interaction', 'daily_target', { videos: value }); }}>{value}</button>
            ))}
          </div>
        </fieldset>
        <div className="output-total" aria-live="polite">
          <span>CAMPAIGN OUTPUT TARGET</span>
          <strong>{total}</strong>
          <p>ready-to-review videos planned across {days} days</p>
        </div>
        <div className="daily-strip" aria-label={`${daily} daily video slots`}>
          {slots.map((slot) => (
            <figure key={slot}>
              <img src="/media/commissioning-poster-v3.webp" alt="GetAutomata campaign video preview" loading="lazy" />
              <figcaption>VIDEO {String(slot).padStart(2, '0')}</figcaption>
            </figure>
          ))}
        </div>
        <button className="primary-action" type="button" onClick={onAccess}>GET EARLY ACCESS <span aria-hidden="true">↗</span></button>
      </div>
    </section>
  );
}

function Ownership() {
  return (
    <section className="ownership-section">
      <div className="section-copy">
        <h2>Buy the Pipeline once. Use it for every campaign.</h2>
        <p>It stays in your library. Credits power the content it produces.</p>
      </div>
      <div className="ownership-flow" aria-label="Pipeline ownership model">
        <div><span>MARKETPLACE</span><strong>Choose a Pipeline</strong></div>
        <div><span>YOUR LIBRARY</span><strong>Own and unlock it</strong></div>
        <div><span>MISSION CONTROL</span><strong>Run campaigns with credits</strong></div>
      </div>
      <div className="control-points">
        <details open>
          <summary>Nothing publishes without approval.<span>+</span></summary>
          <p>You review every output before GetAutomata can release it.</p>
        </details>
        <details>
          <summary>The Pipeline stays yours.<span>+</span></summary>
          <p>A one-time Marketplace purchase unlocks the selected Pipeline and keeps it in your owned library.</p>
        </details>
        <details>
          <summary>Credits power campaign runs.<span>+</span></summary>
          <p>Your subscription allocation or top-ups supply the credits used when the Pipeline produces a campaign.</p>
        </details>
      </div>
    </section>
  );
}

function Questions() {
  return (
    <section className="faq-section">
      <div className="section-copy"><h2>Before you join.</h2></div>
      <div className="faq-list">
        {FAQS.map((item) => (
          <details key={item.question}>
            <summary>{item.question}<span>+</span></summary>
            <p>{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function FinalAccess({ onAccess }: { onAccess: () => void }) {
  return (
    <section className="final-access">
      <div className="final-signal" aria-hidden="true"><i /><i /><i /><i /><i /></div>
      <h2>See GetAutomata before everyone else.</h2>
      <p>Confirm your email. We will show you the system and send your Mission Control invitation when access opens.</p>
      <button className="primary-action" type="button" onClick={onAccess}>GET EARLY ACCESS <span aria-hidden="true">↗</span></button>
    </section>
  );
}

function LaunchWall({ receipt, onReset, onVerify }: { receipt: SignupReceipt; onReset: () => void; onVerify: () => void }) {
  const needsConfirmation = receipt.status === 'confirmation_required';
  const verifying = receipt.status === 'verification_pending';
  const registered = receipt.status === 'registered' || receipt.status === 'preview';
  useEffect(() => { track('launch_wall_viewed', 'launch_wall', { status: receipt.status }); }, [receipt.status]);

  return (
    <main className="launch-wall">
      <header><Brand /><span>PRE-LAUNCH ACCESS</span></header>
      <div className="launch-orbit" aria-hidden="true"><i /><i /><i /></div>
      <section>
        <p className="launch-request">REQUEST {receipt.requestId.slice(0, 8).toUpperCase()}</p>
        <h1>{needsConfirmation ? 'CHECK YOUR INBOX.' : verifying ? 'CONFIRMATION RECEIVED.' : 'YOU ARE ON THE LIST.'}</h1>
        <div className="launch-status">
          <span>{needsConfirmation ? 'EMAIL ACCEPTED' : verifying ? 'VERIFYING RECORD' : 'ACCESS REQUEST REGISTERED'}</span>
          <strong>{needsConfirmation ? 'CONFIRM YOUR ADDRESS' : verifying ? 'ONE MOMENT' : 'PRE-LAUNCH QUEUE'}</strong>
        </div>
        <p>
          {needsConfirmation
            ? 'Use the confirmation button in the email we sent. We will add you to the launch queue when your address is confirmed.'
            : verifying
              ? 'Your confirmation returned. We are checking it against the access queue.'
              : 'Your place is registered. We will send product updates and your Mission Control invitation when your access wave opens.'}
        </p>
        <dl>
          <div><dt>PRODUCT</dt><dd>GETAUTOMATA</dd></div>
          <div><dt>STATUS</dt><dd>FINAL COMMISSIONING</dd></div>
          <div><dt>NEXT</dt><dd>{needsConfirmation ? 'EMAIL CONFIRMATION' : verifying ? 'QUEUE VERIFICATION' : 'LAUNCH INVITATION'}</dd></div>
        </dl>
        {verifying && <button className="primary-action" type="button" onClick={onVerify}>CHECK STATUS <span aria-hidden="true">↻</span></button>}
        {registered && <a className="primary-action" href={DASHBOARD_URL} onClick={() => track('dashboard_handoff', 'launch_wall')}>ALREADY INVITED? SIGN IN <span aria-hidden="true">↗</span></a>}
        <button className="launch-reset" type="button" onClick={onReset}>USE A DIFFERENT EMAIL</button>
      </section>
    </main>
  );
}

export default function App() {
  const [theme, setTheme] = useState<Theme>(() => document.documentElement.dataset.theme === 'day' ? 'day' : 'night');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement>('hero');
  const [receipt, setReceipt] = useState<SignupReceipt | null>(readInitialReceipt);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem('ga-mobile-theme', theme); } catch { /* local theme preference is optional */ }
  }, [theme]);

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(motion.matches);
    sync();
    motion.addEventListener('change', sync);
    return () => motion.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (receipt) return;
    const hero = document.querySelector('.hero');
    if (!hero) return;
    const observer = new IntersectionObserver(([entry]) => setStickyVisible(!entry.isIntersecting), { threshold: 0.08 });
    observer.observe(hero);
    return () => observer.disconnect();
  }, [receipt]);

  function openAccess(nextPlacement: Placement, source: 'hero' | 'header' | 'repeated_cta' | 'mobile_sticky' | 'final') {
    setPlacement(nextPlacement);
    setSheetOpen(true);
    if (source === 'hero') track('hero_cta_click', 'hero');
    else if (source === 'final') track('final_cta', 'final');
    else track('signup_cta', source);
  }

  const verify = useCallback(async (candidate: SignupReceipt | null) => {
    if (!candidate?.requestId) return;
    try {
      const response = await fetch(`/api/early-access/status?receipt=${encodeURIComponent(candidate.requestId)}`, { headers: { Accept: 'application/json' } });
      const payload = await response.json().catch(() => null) as { ok?: boolean; status?: 'registered' | 'confirmation_required' } | null;
      if (response.ok && payload?.ok && payload.status === 'registered') {
        const registered = { ...candidate, status: 'registered' as const };
        persistReceipt(registered);
        setReceipt(registered);
      }
    } catch {
      // The verification button remains available.
    }
  }, []);

  useEffect(() => {
    if (receipt?.status !== 'verification_pending') return;
    persistReceipt(receipt);
    const verificationTimer = window.setTimeout(() => void verify(receipt), 0);
    return () => window.clearTimeout(verificationTimer);
  }, [receipt, verify]);

  if (receipt) {
    return <LaunchWall receipt={receipt} onVerify={() => void verify(receipt)} onReset={() => { persistReceipt(null); setStickyVisible(false); setReceipt(null); history.replaceState(null, '', window.location.pathname); }} />;
  }

  return (
    <>
      <Header theme={theme} onTheme={setTheme} onAccess={() => openAccess('hero', 'header')} />
      <main>
        <Hero reducedMotion={reducedMotion} onAccess={() => openAccess('hero', 'hero')} />
        <PipelineConsole />
        <ProductionFilm reducedMotion={reducedMotion} />
        <OutputCalculator onAccess={() => openAccess('run', 'repeated_cta')} />
        <Ownership />
        <Questions />
        <FinalAccess onAccess={() => openAccess('final', 'final')} />
      </main>
      <footer className="site-footer">
        <Brand />
        <p>Automated content production with human approval.</p>
        <nav aria-label="Footer links">
          <a href={DASHBOARD_URL}>Mission Control sign in</a>
          <a href="https://getautomata.ai/privacy">Privacy</a>
        </nav>
        <small>© {new Date().getFullYear()} GetAutomata</small>
      </footer>
      <button className={stickyVisible ? 'sticky-access is-visible' : 'sticky-access'} type="button" onClick={() => openAccess('run', 'mobile_sticky')}>GET EARLY ACCESS <span aria-hidden="true">↗</span></button>
      <AccessSheet open={sheetOpen} placement={placement} onClose={() => setSheetOpen(false)} onSuccess={(nextReceipt) => { setSheetOpen(false); setReceipt(nextReceipt); }} />
    </>
  );
}
